/**
 * Nicel muhakeme: çift kurallı sayı örüntüsü ve işlem karesi.
 *
 * Dosya adı bir gruplama DEĞİL, yazılma sırası — ikisi de alan
 * tablosunda akıl yürütme (`EGZERSIZ_ALANI`). Bunu bir kez dosya
 * düzenine göre doldurup yanlış ikon basmıştık.
 */

import { Rng } from '../../prng'
import { gradeList } from '../helpers'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { CellNode, ExerciseTemplate, GradeResult, ItemResult } from '../types'

/* ================================================================== *
 * 21. Çift kurallı sayı örüntüsü
 *
 * Tek bir lineer kural yerine iki (yüksek düzeyde üç) kural iç içe
 * geçiyor: 10, 20, 13, 10, 16, 5… — birinci dizi 3 artarken ikincisi
 * 2'ye bölünüyor. Çocuğun iki kuralı aynı anda tutması gerekiyor.
 *
 * ÇÖZÜMÜN TEK OLMASI ŞART ve burada bedava değil: iki dizinin iç içe
 * geçmiş hali kazara tek bir aritmetik diziye benzeyebiliyor
 * (1, 2, 3, 4, 5, 6 → "+1" okuması da savunulabilir, cevap farklı
 * çıkıyor). O durumda sorulan soru ile cevaplanan soru ayrışıyor.
 * Üretim bunu kontrol ediyor: birleşik dizi kendisi bir aritmetik dizi
 * ise tohum ilerletilip yeniden deneniyor.
 * ================================================================== */
export const dualSequence: ExerciseTemplate = {
  code: 'dual_sequence',
  title: 'İç içe diziyi sürdür',
  domains: ['working_memory_verbal', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      rows: scale(l, 3, 6),
      /** Kaç kural iç içe: ikisini tutmak zor, üçü çok zor */
      kurallar: l >= 8 ? 3 : 2,
      /** Çarpma ve bölme kuralları 4. düzeyden sonra giriyor */
      carpma: l >= 4,
      /** Her dizinin kaç terimi görünüyor: üçten az olursa kural çıkarılamaz */
      terim: scale(l, 4, 3),
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const satir = p['rows'] as number
    const kuralSayisi = p['kurallar'] as number
    const terim = p['terim'] as number

    const lines: { label: string; answer: string }[] = []
    for (let r = 0; r < satir; r++) {
      let dizi: number[] | null = null
      let cevap = 0
      // Birkaç deneme: belirsiz çıkan kurgu atılıyor
      for (let deneme = 0; deneme < 40 && dizi === null; deneme++) {
        const aday = kur(rng, kuralSayisi, terim, p['carpma'] as boolean)
        if (!aday) continue
        if (aritmetikMi(aday.gorunen)) continue
        dizi = aday.gorunen
        cevap = aday.sonraki
      }
      if (dizi === null) continue
      lines.push({ label: `${dizi.join(', ')}, ___`, answer: String(cevap) })
    }

    return {
      code: 'dual_sequence',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        kuralSayisi === 2
          ? 'Bu dizilerde İKİ kural iç içe: bir sayı atlayarak bak. Sıradaki sayıyı yaz.'
          : 'Bu dizilerde ÜÇ kural iç içe: ikişer sayı atlayarak bak. Sıradaki sayıyı yaz.',
      render: [
        { t: 'rows', items: lines.map((l, i) => ({ label: l.label, blank: true, id: 'd' + i })) },
      ],
      solution: lines.map((l) => l.answer),
      grading: 'auto',
      estimatedSeconds: lines.length * 40,
      itemCount: lines.length,
    }
  },
  grade(instance, answer) {
    return gradeList(instance.solution as string[], answer)
  },
}

/** Tek bir satırın kurgusu; olmazsa null döner ve çağıran yeniden dener. */
function kur(
  rng: Rng,
  kuralSayisi: number,
  terim: number,
  carpma: boolean,
): { gorunen: number[]; sonraki: number } | null {
  type Kural = { bas: number; tip: 'ekle' | 'carp' | 'bol'; d: number }
  const kurallar: Kural[] = []
  for (let k = 0; k < kuralSayisi; k++) {
    const tipler: Kural['tip'][] = carpma ? ['ekle', 'carp', 'bol'] : ['ekle']
    const tip = rng.pick(tipler)
    if (tip === 'ekle') {
      kurallar.push({ bas: rng.int(2, 40), tip, d: rng.int(2, 9) })
    } else if (tip === 'carp') {
      kurallar.push({ bas: rng.int(1, 4), tip, d: rng.int(2, 3) })
    } else {
      // Bölme: başlangıç, tam bölünerek sonuna kadar gidecek kadar büyük
      const d = rng.int(2, 3)
      const bas = d ** (terim + 1) * rng.int(1, 3)
      kurallar.push({ bas, tip, d })
    }
  }

  // İki kural birbirinin aynısıysa iç içe geçmiş bir şey yok
  const imzalar = kurallar.map((k) => `${k.tip}${k.d}`)
  if (new Set(imzalar).size < kuralSayisi) return null

  const diziler = kurallar.map((k) => {
    const out = [k.bas]
    for (let i = 1; i <= terim; i++) {
      const o = out[i - 1]!
      out.push(k.tip === 'ekle' ? o + k.d : k.tip === 'carp' ? o * k.d : o / k.d)
    }
    return out
  })

  // Hiçbir terim kesirli ya da taşkın olmasın: çocuk tam sayı bekliyor
  for (const d of diziler) {
    for (const v of d) {
      if (!Number.isInteger(v) || v < 1 || v > 999) return null
    }
  }

  // İç içe geç: a0 b0 a1 b1 … son görünen terimden sonraki soruluyor
  const gorunen: number[] = []
  for (let i = 0; i < terim; i++) {
    for (let k = 0; k < kuralSayisi; k++) gorunen.push(diziler[k]![i]!)
  }
  const sonraki = diziler[0]![terim]!

  // Aynı sayının iki kez geçmesi ipucu veriyor gibi görünüp
  // şaşırtıyor: "hangi diziye ait" sorusu cevapsız kalıyor
  if (new Set(gorunen).size !== gorunen.length) return null

  return { gorunen, sonraki }
}

/**
 * Birleşik dizi kendisi bir aritmetik dizi mi.
 *
 * Öyleyse "tek kural" okuması da tutarlı ve iki ayrı cevap savunulabilir
 * hale geliyor. Böyle bir sayfa çocuğun hatası değil bizim hatamız.
 */
function aritmetikMi(d: number[]): boolean {
  if (d.length < 3) return true
  const fark = d[1]! - d[0]!
  return d.every((v, i) => i === 0 || v - d[i - 1]! === fark)
}

/* ================================================================== *
 * 22. İşlem karesi — içi boşaltılmış aritmetik ağ
 *
 * Satır ve sütun sonuçları verili, aradaki sayıların ya da işaretlerin
 * bir kısmı boş. Çocuk tersine işlem yapıyor.
 *
 * ÇARPMA KASITLI OLARAK YOK. Üç terimli bir satırda `2 + 3 × 4`
 * yazdığımız an iki okuma çıkıyor (soldan sağa 20, öncelikle 14) ve
 * hangisini beklediğimiz yönergede yazsa bile çocuğun okuluna göre
 * değişen bir şeye dayanmış oluyoruz. Toplama ve çıkarmada soldan sağa
 * okuma ile işlem önceliği AYNI sonucu veriyor, yani belirsizlik yok.
 * Zorluk ızgara boyundan, sayı aralığından ve boş bırakılan şeyin
 * türünden geliyor.
 *
 * TEK ÇÖZÜM GARANTİSİ yerleşim kuralından: her satırda ve her sütunda en
 * fazla bir boşluk var. O boşluk kendi satır denkleminden tek değerle
 * çıkıyor. Satırda iki boşluk olsaydı sonsuz çözüm olurdu ve cevap
 * anahtarı yalan söylerdi.
 * ================================================================== */
export const arithGrid: ExerciseTemplate = {
  code: 'arith_grid',
  title: 'İşlem karesi',
  domains: ['processing_speed', 'working_memory_verbal'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Izgara kenarı: 2×2'den 4×4'e */
      n: l >= 8 ? 4 : l >= 4 ? 3 : 2,
      /** Sayı aralığının üst sınırı */
      enbuyuk: scale(l, 9, 30),
      /** Çıkarma 2. düzeyden sonra */
      cikarma: l >= 2,
      /** İşaret boşlukları 5. düzeyden sonra */
      isaretBoslugu: l >= 5,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const n = p['n'] as number
    const enbuyuk = p['enbuyuk'] as number
    const isaretler: ('+' | '-')[] = p['cikarma'] ? ['+', '-'] : ['+']

    /**
     * Sayılar rastgele; işaretler ONARILIYOR.
     *
     * İlk hali "rastgele kur, ara sonuç negatife düşerse at, yeniden
     * dene" idi ve 4×4 ızgarada sekiz denklemin hepsinin aynı anda
     * tutması gerektiği için iki yüz denemede bir kurgu bulamıyordu:
     * `arith_grid: kurgu bulunamadi`. Reddet-ve-yeniden-dene kalıbı,
     * çözüm kümesinin pratikte boş olabildiği yerde kullanılmaz —
     * aritmetik akıcılığında aynı hatayı bir kez yapmışız.
     *
     * Onarım yerel olabiliyor çünkü satır işaretleri yalnızca satırı,
     * sütun işaretleri yalnızca sütunu etkiliyor: soldan sağa giderken
     * toplam negatife düşecekse o işaret artıya çevriliyor. Artı hiçbir
     * zaman toplamı düşürmediği için bir geçiş yeterli.
     *
     * Bedeli, eksi işaretlerinin bir kısmının artıya dönmesi: küçük
     * sayılarla kurulan satırlarda eksi beklenenden az çıkıyor. Bunun
     * karşılığı, negatif ara sonuç görmeyen bir çocuğun sayfada
     * negatif sayı görmemesi.
     */
    const sayi: number[] = []
    for (let i = 0; i < n * n; i++) sayi.push(rng.int(1, enbuyuk))

    const satirOp: ('+' | '-')[][] = []
    for (let r = 0; r < n; r++) {
      const ops = Array.from({ length: n - 1 }, () => rng.pick(isaretler))
      let t = sayi[r * n]!
      for (let k = 0; k < ops.length; k++) {
        const sonraki = sayi[r * n + k + 1]!
        if (ops[k] === '-' && t - sonraki < 0) ops[k] = '+'
        t = ops[k] === '+' ? t + sonraki : t - sonraki
      }
      satirOp.push(ops)
    }

    const sutunOp: ('+' | '-')[][] = []
    for (let c = 0; c < n; c++) {
      const ops = Array.from({ length: n - 1 }, () => rng.pick(isaretler))
      let t = sayi[c]!
      for (let k = 0; k < ops.length; k++) {
        const sonraki = sayi[(k + 1) * n + c]!
        if (ops[k] === '-' && t - sonraki < 0) ops[k] = '+'
        t = ops[k] === '+' ? t + sonraki : t - sonraki
      }
      sutunOp.push(ops)
    }

    const kurgu = { sayi, satirOp, sutunOp }

    const satirSonuc = Array.from(
      { length: n },
      (_, r) => hesap(kurgu.sayi.slice(r * n, r * n + n), kurgu.satirOp[r]!),
    )
    const sutunSonuc = Array.from({ length: n }, (_, c) =>
      hesap(
        Array.from({ length: n }, (_, r) => kurgu.sayi[r * n + c]!),
        kurgu.sutunOp[c]!,
      ),
    )

    /**
     * Boşluk yerleşimi: satır başına ve sütun başına en fazla bir tane.
     * Rastgele bir permütasyon tam olarak bunu veriyor.
     */
    const perm = rng.shuffle(Array.from({ length: n }, (_, i) => i))
    const sayiBosluk = new Set<number>()
    const isaretBosluk = new Set<string>()
    for (let r = 0; r < n; r++) {
      // İşaret boşluğu olan satırda sayı boşluğu YOK: ikisi birlikte
      // olursa denklem iki bilinmeyenli ve çözüm tek değil
      if (p['isaretBoslugu'] && r % 2 === 1) {
        isaretBosluk.add(`s${r}:${rng.int(0, n - 2)}`)
      } else {
        sayiBosluk.add(r * n + perm[r]!)
      }
    }

    const boy = 2 * n + 1
    const cells: CellNode[] = Array.from({ length: boy * boy }, () => ({}))
    const koy = (r: number, c: number, h: CellNode) => {
      cells[r * boy + c] = h
    }
    const cevaplar: { id: string; beklenen: string }[] = []

    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        const i = r * n + c
        if (sayiBosluk.has(i)) {
          koy(2 * r, 2 * c, { blank: true, id: 'h' + i })
          cevaplar.push({ id: 'h' + i, beklenen: String(kurgu.sayi[i]) })
        } else {
          koy(2 * r, 2 * c, { glyph: String(kurgu.sayi[i]) })
        }
      }
      for (let k = 0; k < n - 1; k++) {
        const anahtar = `s${r}:${k}`
        if (isaretBosluk.has(anahtar)) {
          koy(2 * r, 2 * k + 1, { blank: true, id: 'o' + anahtar })
          cevaplar.push({ id: 'o' + anahtar, beklenen: kurgu.satirOp[r]![k]! })
        } else {
          koy(2 * r, 2 * k + 1, { glyph: kurgu.satirOp[r]![k]! })
        }
      }
      koy(2 * r, 2 * n - 1, { glyph: '=' })
      koy(2 * r, 2 * n, { glyph: String(satirSonuc[r]), marked: true })
    }
    for (let c = 0; c < n; c++) {
      for (let k = 0; k < n - 1; k++) {
        koy(2 * k + 1, 2 * c, { glyph: kurgu.sutunOp[c]![k]! })
      }
      koy(2 * n - 1, 2 * c, { glyph: '=' })
      koy(2 * n, 2 * c, { glyph: String(sutunSonuc[c]), marked: true })
    }

    const yonerge = isaretBosluk.size
      ? 'Boş kutulara gelen sayıyı, boş yuvarlaklara gelen işareti yaz. Hem satırlar hem sütunlar doğru çıkmalı.'
      : 'Boş kutulara gelen sayıyı yaz. Hem satırlar hem sütunlar doğru çıkmalı.'

    return {
      code: 'arith_grid',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: yonerge,
      render: [
        /*
         * Çerçeve YOK.
         *
         * `border: true` ile her hücre kutulanıyor ve ızgaranın tek
         * sayılı satır/sütunlarındaki dolgu hücreleri de boş kutu
         * olarak basılıyordu: sayfada işlemin iki katı kadar anlamsız
         * kare duruyordu. Cevap kutusunu `blank` zaten çiziyor.
         */
        { t: 'grid', cols: boy, rows: boy, cells, border: false },
        {
          /**
           * Palet: ekranda cevap klavyeyle değil dokunarak giriliyor.
           * İşaret boşluğu varsa `+` ve `-` de paletin içinde.
           */
          t: 'rows',
          items: [],
          palette: [
            ...Array.from({ length: Math.min(enbuyuk, 30) }, (_, i) => ({ glyph: String(i + 1) })),
            ...(isaretBosluk.size ? isaretler.map((o) => ({ glyph: o })) : []),
          ],
        },
      ],
      solution: cevaplar,
      grading: 'auto',
      estimatedSeconds: cevaplar.length * 45,
      itemCount: cevaplar.length,
    }
  },
  grade(instance, answer): GradeResult {
    const beklenen = instance.solution as { id: string; beklenen: string }[]
    const got = (answer ?? {}) as Record<string, unknown>
    const items: ItemResult[] = beklenen.map((b) => {
      const ham = got[b.id]
      const verilen = ham === undefined || ham === null || String(ham) === '' ? null : String(ham)
      return { id: b.id, beklenen: b.beklenen, verilen, dogru: verilen === b.beklenen }
    })
    const perItem = items.map((x) => x.dogru)
    const correct = perItem.filter(Boolean).length
    return {
      correct,
      total: items.length,
      accuracy: items.length ? correct / items.length : 0,
      perItem,
      items,
    }
  },
  /**
   * Boşlukları satır–sütun koordinatıyla yazıyor.
   *
   * Varsayılan biçimleyici `h7` ve `os1:1` gibi iç kimlikleri
   * basıyordu; uygulayıcının elinde sayfa var, kimlik yok.
   */
  anahtar(instance) {
    const n = instance.params['n'] as number
    return (instance.solution as { id: string; beklenen: string }[])
      .map((x) => {
        if (x.id.startsWith('h')) {
          const i = Number(x.id.slice(1))
          return `${Math.floor(i / n) + 1}. satır ${(i % n) + 1}. kutu: ${x.beklenen}`
        }
        const m = x.id.match(/^os(\d+):(\d+)$/)
        if (!m) return `${x.id}: ${x.beklenen}`
        return `${Number(m[1]) + 1}. satır ${Number(m[2]) + 1}. işaret: ${x.beklenen}`
      })
      .join('  ·  ')
  },
}

/**
 * Soldan sağa toplama/çıkarma.
 *
 * Toplama ve çıkarmada soldan sağa okuma işlem önceliğiyle aynı sonucu
 * verdiği için burada belirsizlik yok — çarpmanın dışarıda bırakılma
 * sebebi de bu. İşaretler üretimde onarıldığı için ara sonuç negatife
 * düşmüyor; yine de bir gün düşerse sessizce geçmesin.
 */
function hesap(sayilar: number[], islemler: ('+' | '-')[]): number {
  let t = sayilar[0]!
  for (let i = 0; i < islemler.length; i++) {
    t = islemler[i] === '+' ? t + sayilar[i + 1]! : t - sayilar[i + 1]!
    if (t < 0) throw new Error('arith_grid: ara sonuc negatif')
  }
  return t
}
